import type { Content } from "@prismicio/client";
import type { SliceComponentProps } from "@prismicio/react";
import { wrapIframe } from "@commercekit/shared";
import { useFetcher } from "@remix-run/react";
import { useLocalePath } from "@commercekit/remix";
import { useEffect, useMemo } from "react";
import type { loader as liveSignupCountLoader } from "~/routes-server/resources/live-signup-count.server";
import BoxedTimer from "./variations/BoxedTimer";
import DefaultTimer from "./variations/DefaultTimer";
import FullWidthTimer from "./variations/FullWidthTimer";
import WovarBrandTimer from "./variations/WovarBrandTimer";

export type CountdownTimerProps =
  SliceComponentProps<Content.CountdownTimerSlice>;

const toHtml = (raw: unknown): string =>
  // side_text is a raw-HTML Text field. Existing/unpublished documents can still return a
  // rich-text array, which would crash wrapIframe (array has no .replace). Strings only.
  typeof raw === "string" ? wrapIframe(raw) || "" : "";

// The boxed counter's label/caption are plain text. Strip any stray tags a CMS value might
// still contain so leftover HTML renders as text and can never break the card's structure.
const stripTags = (raw: unknown): string =>
  typeof raw === "string" ? raw.replace(/<[^>]*>/g, "").trim() : "";

const CountdownTimer = ({ slice }: CountdownTimerProps) => {
  const isBoxed = slice.variation === "boxed";

  // The boxed variant shows a live global signup count fetched from the resource route, so
  // the slice works on any page it's dropped into without per-page loader wiring.
  const fetcher = useFetcher<typeof liveSignupCountLoader>();
  const localePath = useLocalePath();

  useEffect(() => {
    if (isBoxed && fetcher.state === "idle" && fetcher.data === undefined) {
      fetcher.load(localePath("/resources/live-signup-count"));
    }
  }, [isBoxed, localePath, fetcher]);

  const liveSignupCount = fetcher.data?.liveSignupCount ?? null;

  // Free-form raw-HTML side content for the default / wovarBrand variations.
  const sideText = useMemo(() => {
    if (slice.variation === "boxed" || slice.variation === "fullWidth") {
      return "";
    }
    return toHtml(slice.primary.side_text);
  }, [slice]);

  // Boxed variant's live signup counter: a label, the live number, and a caption.
  // Structure and styling live in code/CSS (.countdown-timer__counter* in custom.css);
  // editors only supply the label and caption text (plain text — see stripTags).
  const counterBox = useMemo(() => {
    if (slice.variation !== "boxed") return null;
    const label = stripTags(slice.primary.counter_top_text);
    const caption = stripTags(slice.primary.counter_bottom_text);
    if (!label && !caption) return null;

    return (
      <div className="countdown-timer__counter">
        {label && (
          <div className="countdown-timer__counter-label">
            <span className="countdown-timer__counter-dot" aria-hidden="true" />
            {label}
          </div>
        )}
        <div
          id="transfer-count"
          className="countdown-timer__counter-value"
          data-target={liveSignupCount?.raw ?? 0}
        >
          {liveSignupCount?.formatted ?? "0"}
        </div>
        {caption && (
          <div className="countdown-timer__counter-caption">{caption}</div>
        )}
      </div>
    );
  }, [slice, liveSignupCount]);

  const hasSideContent = Boolean(sideText) || Boolean(counterBox);

  const timer = (() => {
    switch (slice.variation) {
      case "wovarBrand":
        return <WovarBrandTimer slice={slice} />;
      case "boxed":
        return <BoxedTimer slice={slice} />;
      case "fullWidth":
        return <FullWidthTimer slice={slice} />;
      case "default":
      default:
        return <DefaultTimer slice={slice} />;
    }
  })();

  const sectionClassName = (() => {
    if (slice.variation === "fullWidth") {
      return "countdown-timer-full";
    }

    if (hasSideContent) {
      return slice.variation === "boxed"
        ? "countdown-timer countdown-timer--boxed countdown-timer--with-side-text"
        : "countdown-timer countdown-timer--with-side-text";
    }

    if (slice.variation === "wovarBrand") {
      return "ct-brand";
    }

    return slice.variation === "boxed"
      ? "countdown-timer countdown-timer--boxed"
      : "countdown-timer";
  })();

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className={sectionClassName}
    >
      {!hasSideContent ? (
        timer
      ) : counterBox ? (
        // Boxed live counter: wrap in the same Bootstrap .container the sibling page
        // sections use, so the two boxes line up with them and share their width. The inner
        // grid keeps the columns equal on desktop and stacks them on mobile.
        <div className="container">
          <div className="countdown-timer__layout">
            <div className="countdown-timer__timer">{timer}</div>
            <div className="countdown-timer__side-text">{counterBox}</div>
          </div>
        </div>
      ) : (
        <div className="countdown-timer__layout">
          <div className="countdown-timer__timer">
            {slice.variation === "wovarBrand" ? (
              <div className="ct-brand">{timer}</div>
            ) : (
              timer
            )}
          </div>
          <div
            className="countdown-timer__side-text content"
            dangerouslySetInnerHTML={{ __html: sideText }}
          />
        </div>
      )}
    </section>
  );
};

export default CountdownTimer;
